import { ManifestDraft, ManifestReceived, ManifestSent } from '../../constants';

export class Home {
  constructor({ id, name, departmentIds, assets = [], manifests = [] }) {
    this.id = id;
    this.name = name;
    this.assets = assets.map((a) => new Asset(a));
    this.manifests = manifests.map((m) => new Manifest(m));
    this.departmentIds = departmentIds;
  }

  /**
   * Gets all assets that are not deleted, in selected departments.
   * @param {Array<string>} storageIds - A list of department IDs to filter on. Usally only 1 department, or the office and its departments.
   * @returns {Array<Object>} The filtered list of assets.
   */
  getAllAssets(storageIds) {
    if (storageIds != null && storageIds.length > 0) {
      const storageIdSet = new Set(storageIds); // Convert storageIds to a Set for fast lookup.
      return this.assets.filter((a) => {
        if (a.deleted) return false; // Skip if asset is deleted.
        // Check if any lineage exists in the storageIdSet.
        return a.lineage.some((lineageId) => storageIdSet.has(lineageId));
      });
    }
    return [];
  }
  /**
   * Gets only assets that have warnings, and a total count of all assets, within selected departments.
   * @param {Array<string>} storageIds - A list of department IDs to filter on. Usally only 1 department, or the office and its departments.
   * @returns {Object} Categorized assets with the following structure:
   *   - `expiredAssets` {Object[]} - Assets whose calibration is overdue.
   *   - `expiringAssets` {Object[]} - Assets whose calibration is due within 90 days.
   *   - `rentalAssets` {Object[]} - Assets that are marked as rentals.
   *   - `count` {number} - Total count of assets matching the criteria.
   */
  getAssetsCategorized(storageIds) {
    const timeNow = Date.now();
    const day = 86400000;
    const storageIdSet = new Set(storageIds);
    return this.assets.reduce(
      (result, a) => {
        if (a.deleted || !a.lineage.some((id) => storageIdSet.has(id))) return result;

        result.count++;
        if (a.product.calibrationInterval > 0) {
          const expiringDate = a.calibrationDueDate?.toDate() || 0;

          if (expiringDate < timeNow) {
            result.expiredAssets.push(a);
          } else if (expiringDate < timeNow + 90 * day) {
            result.expiringAssets.push(a);
          }
        }
        if (a.rental) {
          result.rentalAssets.push(a);
        }

        return result;
      },
      {
        expiredAssets: [],
        expiringAssets: [],
        rentalAssets: [],
        count: 0,
      },
    );
  }
  /**
   * Gets all incoming Manifests from home, filter on selected departments
   * @param {Array<string>} storageIds - A list of department IDs to filter on. Usally only 1 department, or the office and its departments.
   * @returns {Array<Object>} The filtered list of incoming manifests from home.
   */
  getIncomingManifests(storageIdsList) {
    if (storageIdsList != null && storageIdsList.length > 0) {
      return this.manifests
        .filter((m) => m.isRecipient(storageIdsList) && !m.isDraft())
        .sort((a, b) => b.date.seconds - a.date.seconds);
    }
    return [];
  }
  /**
   * Gets all outgoing Manifests from home, filter on selected departments
   * @param {Array<string>} storageIds - A list of department IDs to filter on. Usally only 1 department, or the office and its departments.
   * @returns {Array<Object>} The filtered list of outgoing manifests from home.
   */
  getOutgoingManifests(storageIdsList) {
    if (storageIdsList != null && storageIdsList.length > 0) {
      return this.manifests
        .filter((m) => m.isSender(storageIdsList))
        .sort((a, b) => b.date.seconds - a.date.seconds);
    }
    return [];
  }
}

class Asset {
  constructor({
    id,
    calibrationDate = null,
    calibrationDueDate = null,
    deleted = false,
    product = {},
    storageId = null,
    lineage = null,
    rental = false,
    owner,
    tagNumber,
    serialNumber,
  }) {
    this.id = id;
    this.calibrationDate = calibrationDate;
    this.calibrationDueDate = calibrationDueDate;
    this.deleted = deleted;
    this.product = new Product(product);
    this.storageId = storageId;
    this.rental = rental;
    this.lineage = lineage;
    this.owner = owner;
    this.tagNumber = tagNumber;
    this.serialNumber = serialNumber;
  }
}

class Product {
  constructor({ id, name, calibrationInterval = 0 }) {
    this.id = id;
    this.name = name;
    this.calibrationInterval = calibrationInterval;
  }
}

class Manifest {
  constructor({ id, name, project, date, recipient, reference, sender, state, stateUpdated }) {
    this.id = id;
    this.name = name;
    this.project = project;
    this.date = date;
    this.recipient = new Target({ ...recipient });
    this.reference = reference;
    this.sender = new Target({ ...sender });
    this.state = state;
    this.stateUpdated = stateUpdated;
  }

  isSender(storageIds) {
    return storageIds.includes(this.sender.id);
  }

  isRecipient(storageIds) {
    return storageIds.includes(this.recipient.id);
  }

  isSent() {
    return this.state === ManifestSent;
  }

  isDraft() {
    return this.state === ManifestDraft;
  }

  isReceived() {
    return this.state === ManifestReceived;
  }
}

class Target {
  constructor({ id, name }) {
    this.id = id;
    this.name = name;
  }
}
